import { useState, useCallback, useEffect, useRef } from 'react';
import { APIProvider, Map, Marker, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

interface LocationPickerProps {
  onLocationSelect: (latitude: number, longitude: number, address?: string) => void;
  initialCenter?: { lat: number; lng: number };
  height?: string;
  showSearch?: boolean;
  /**
   * Posición controlada desde afuera (por ejemplo, cuando el usuario escribe
   * latitud/longitud a mano en un input). Si cambia, el mapa recentra y el
   * marcador se mueve, igual que con un clic o una búsqueda.
   */
  position?: { lat: number; lng: number } | null;
  /**
   * Si es `true`, el mapa ignora `height` y en cambio ocupa TODO el alto del
   * contenedor donde se lo monte (vía flexbox, `flex-1`) — para "homologar"
   * su alto con el de una columna vecina (ej. un formulario) en vez de un
   * valor fijo en px que puede quedar más alto o más bajo que esa columna.
   * El contenedor padre debe darle una altura real (ej. con `items-stretch`
   * en un `flex` fila, o una altura explícita).
   */
  fillParent?: boolean;
}

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

// Default center: PANAMÁ City
const DEFAULT_CENTER = { lat: 9.0765, lng: -79.5309 };
const DEFAULT_ZOOM = 11;

// Search component that uses Places API
function PlacesSearch({
  onPlaceSelect
}: {
  onPlaceSelect: (lat: number, lng: number, address: string) => void
}) {
  const map = useMap();
  const placesLib = useMapsLibrary('places');
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fix z-index for autocomplete dropdown (it renders outside React)
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .pac-container {
        z-index: 999999 !important;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    console.log('🔍 PlacesSearch: placesLib loaded?', !!placesLib);
    console.log('🔍 PlacesSearch: inputRef ready?', !!inputRef.current);
    console.log('🔍 PlacesSearch: autocomplete exists?', !!autocompleteRef.current);

    if (!placesLib) {
      console.log('⏳ Waiting for Places library to load...');
      return;
    }

    if (!inputRef.current) {
      console.log('⏳ Waiting for input ref...');
      return;
    }

    if (autocompleteRef.current) {
      console.log('✅ Autocomplete already initialized');
      setIsLoading(false);
      return;
    }

    try {
      console.log('🚀 Initializing Google Places Autocomplete...');

      // Initialize autocomplete
      autocompleteRef.current = new placesLib.Autocomplete(inputRef.current, {
        fields: ['geometry', 'formatted_address', 'name'],
        // Bias results towards Panama
        componentRestrictions: { country: 'pa' },
      });

      console.log('✅ Autocomplete initialized successfully');
      setIsLoading(false);
      setError(null);

      // Listen for place selection
      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current?.getPlace();
        console.log('📍 Place selected:', place);

        if (place?.geometry?.location) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          const address = place.formatted_address || place.name || '';

          console.log('📍 Coordinates:', { lat, lng, address });

          // Move map to selected location
          if (map) {
            map.panTo({ lat, lng });
            map.setZoom(17);
          }

          onPlaceSelect(lat, lng, address);
        } else {
          console.warn('⚠️ No geometry in selected place');
        }
      });
    } catch (err) {
      console.error('❌ Error initializing autocomplete:', err);
      setError('Error al inicializar búsqueda. Verifica que Places API esté habilitada.');
      setIsLoading(false);
    }

    return () => {
      // Cleanup
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [placesLib, map, onPlaceSelect]);

  return (
    <div className="relative">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <MagnifyingGlassIcon className="h-5 w-5 text-secondary-400" />
      </div>
      <input
        ref={inputRef}
        type="text"
        placeholder={isLoading ? "Cargando búsqueda..." : "Buscar dirección..."}
        disabled={isLoading}
        className="w-full pl-10 pr-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white placeholder-secondary-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50 disabled:cursor-wait"
      />
      {error && (
        <p className="mt-1 text-xs text-red-500">{error}</p>
      )}
    </div>
  );
}

// Inner map component that can access map context
function MapWithSearch({
  initialCenter,
  height,
  showSearch,
  onLocationSelect,
  markerPosition,
  setMarkerPosition,
  externalPosition,
  fillParent,
}: {
  initialCenter: { lat: number; lng: number };
  height: string;
  showSearch: boolean;
  onLocationSelect: (latitude: number, longitude: number, address?: string) => void;
  markerPosition: { lat: number; lng: number } | null;
  setMarkerPosition: (pos: { lat: number; lng: number } | null) => void;
  externalPosition?: { lat: number; lng: number } | null;
  fillParent?: boolean;
}) {
  const [selectedAddress, setSelectedAddress] = useState<string>('');
  const map = useMap();

  // Sincroniza el mapa cuando la posición cambia desde afuera (ej. el usuario
  // escribió latitud/longitud a mano) y no por un clic o búsqueda locales.
  useEffect(() => {
    if (!externalPosition) return;
    const sinCambios =
      markerPosition &&
      markerPosition.lat === externalPosition.lat &&
      markerPosition.lng === externalPosition.lng;
    if (sinCambios) return;

    setMarkerPosition(externalPosition);
    if (map) {
      map.panTo(externalPosition);
      map.setZoom(17);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalPosition, map]);

  const handleMapClick = useCallback(
    (event: any) => {
      let lat, lng;

      if (event?.detail?.latLng) {
        lat = event.detail.latLng.lat;
        lng = event.detail.latLng.lng;
      } else if (event?.latLng) {
        lat = typeof event.latLng.lat === 'function' ? event.latLng.lat() : event.latLng.lat;
        lng = typeof event.latLng.lng === 'function' ? event.latLng.lng() : event.latLng.lng;
      }

      if (lat && lng) {
        setMarkerPosition({ lat, lng });
        setSelectedAddress('');
        onLocationSelect(lat, lng);
      }
    },
    [onLocationSelect, setMarkerPosition]
  );

  const handlePlaceSelect = useCallback(
    (lat: number, lng: number, address: string) => {
      setMarkerPosition({ lat, lng });
      setSelectedAddress(address);
      onLocationSelect(lat, lng, address);
    },
    [onLocationSelect, setMarkerPosition]
  );

  return (
    <div className={fillParent ? 'flex h-full flex-col space-y-3' : 'space-y-3'}>
      {showSearch && (
        <PlacesSearch onPlaceSelect={handlePlaceSelect} />
      )}

      {selectedAddress && (
        <div className="text-sm text-secondary-600 dark:text-secondary-400 bg-secondary-100 dark:bg-secondary-700 px-3 py-2 rounded-lg">
          <span className="font-medium">Dirección:</span> {selectedAddress}
        </div>
      )}

      <div className={fillParent ? 'min-h-0 flex-1' : undefined} style={fillParent ? undefined : { height }}>
        <Map
          // `defaultCenter` es no-controlado (solo se lee al montar): si ya
          // hay una posición (ej. al volver a este paso del wizard con
          // coordenadas ya cargadas), el mapa abre centrado ahí en vez de
          // Ciudad de Panamá.
          defaultCenter={markerPosition ?? initialCenter}
          defaultZoom={DEFAULT_ZOOM}
          gestureHandling="greedy"
          disableDefaultUI={false}
          onClick={handleMapClick}
          style={{ width: '100%', height: '100%' }}
        >
          {markerPosition && (
            <Marker
              position={markerPosition}
              title="Ubicación seleccionada"
            />
          )}
        </Map>
      </div>
    </div>
  );
}

export function LocationPicker({
  onLocationSelect,
  initialCenter = DEFAULT_CENTER,
  height = '400px',
  showSearch = false, // Disabled by default until Places API is enabled
  position = null,
  fillParent = false,
}: LocationPickerProps) {
  const [markerPosition, setMarkerPosition] = useState<{ lat: number; lng: number } | null>(
    position
  );
  const [mapError, setMapError] = useState<string | null>(null);

  useEffect(() => {
    if (!GOOGLE_MAPS_API_KEY || GOOGLE_MAPS_API_KEY.length < 30) {
      console.warn('Google Maps API Key not configured properly');
    }
  }, []);

  // Si no hay API key, mostrar instrucciones (validación más permisiva)
  if (!GOOGLE_MAPS_API_KEY || GOOGLE_MAPS_API_KEY.length < 30) {
    return (
      <div
        className={`w-full bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6 ${fillParent ? 'h-full' : ''}`}
        style={fillParent ? undefined : { height }}
      >
        <div className="text-center space-y-4">
          <h3 className="text-lg font-semibold text-yellow-800 dark:text-yellow-200">
            Configuración de Google Maps requerida
          </h3>
          <p className="text-sm text-yellow-700 dark:text-yellow-300">
            Para usar el mapa interactivo, necesitas configurar una clave de API de Google Maps:
          </p>
          <ol className="text-left text-sm text-yellow-700 dark:text-yellow-300 space-y-2 max-w-md mx-auto">
            <li>1. Ve a <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer" className="underline font-semibold">Google Cloud Console</a></li>
            <li>2. Crea un proyecto o selecciona uno existente</li>
            <li>3. Habilita "Maps JavaScript API"</li>
            <li>4. Crea una clave de API en "Credenciales"</li>
            <li>5. Agrega la clave al archivo .env como VITE_GOOGLE_MAPS_API_KEY</li>
            <li>6. Reinicia el servidor de desarrollo</li>
          </ol>
          <div className="mt-4 p-3 bg-yellow-100 dark:bg-yellow-900/40 rounded text-xs text-yellow-800 dark:text-yellow-200 font-mono">
            Clave actual: {GOOGLE_MAPS_API_KEY || 'No configurada'}
            <br />
            Longitud: {GOOGLE_MAPS_API_KEY?.length || 0} caracteres (esperado: ~39)
            <br />
            <br />
            <strong>Importante:</strong> Después de actualizar .env, debes reiniciar el servidor:
            <br />
            Detén con Ctrl+C y ejecuta: npm run dev
          </div>
        </div>
      </div>
    );
  }

  if (mapError) {
    return (
      <div
        className={`w-full bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 ${fillParent ? 'h-full' : ''}`}
        style={fillParent ? undefined : { height }}
      >
        <div className="text-center space-y-4">
          <h3 className="text-lg font-semibold text-red-800 dark:text-red-200">
            Error al cargar el mapa
          </h3>
          <p className="text-sm text-red-700 dark:text-red-300">
            {mapError}
          </p>
          <button
            onClick={() => setMapError(null)}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={fillParent ? 'w-full h-full flex flex-col' : 'w-full'}>
      <APIProvider
        apiKey={GOOGLE_MAPS_API_KEY}
        onLoad={() => console.log('Maps API loaded successfully')}
        onError={(error) => {
          console.error('Maps API error:', error);
          setMapError(`Error al cargar Google Maps. Verifica que la clave de API tenga Maps JavaScript API habilitada.`);
        }}
      >
        <MapWithSearch
          initialCenter={initialCenter}
          height={height}
          showSearch={showSearch}
          onLocationSelect={onLocationSelect}
          markerPosition={markerPosition}
          setMarkerPosition={setMarkerPosition}
          externalPosition={position}
          fillParent={fillParent}
        />
      </APIProvider>
    </div>
  );
}
